/**
 * Comprehensive transform utilities for API ↔ UI data conversion
 *
 * Eliminates the need for 28+ individual transform functions by providing
 * reusable transformation patterns for common entity types.
 *
 * @module transformers
 */

import { formatRelativeTime, formatJoinedTime } from './dateFormat'
import {
  PROJECT_STATUS,
  AGENT_STATUS,
  RESOURCE_STATUS,
  KNOWLEDGE_SYNC_STATUS,
  SIMULATION_STATUS,
} from '../constants/status'
import type {
  User,
  Role,
  Environment,
  Prompt,
  Tool,
  RegisteredTable,
  Model,
  Project,
  Agent,
  Simulation,
} from '../services/api/types'

// Base interfaces for common transform patterns
export interface BaseApiEntity {
  id: string
  created_at: number
  updated_at: number
  created_by?: string
  updated_by?: string
  creator_name?: string | null
}

export interface BaseUIEntity {
  id: string
  created: number
  updated: number
  creator: string
  /** Per-user favorite flag, carried through from the API so row stars render. */
  is_fav?: boolean
}

// Extend Tool and RegisteredTable to include BaseApiEntity properties
type ToolWithBase = Tool & BaseApiEntity
type RegisteredTableWithBase = RegisteredTable & BaseApiEntity
type PromptWithBase = Prompt & BaseApiEntity
type ModelWithBase = Model & BaseApiEntity
type ProjectWithBase = Project & BaseApiEntity
type AgentWithBase = Agent & BaseApiEntity

// PermissionDisplay type for role transformers
interface PermissionDisplay {
  feature: string
  actions: Record<string, boolean>
}

// Remove duplicate Environment interface - it's imported from api.ts

// Common status mappings used across entities
// Maps backend status values to display-friendly labels
// Uses centralized constants where possible
export const STATUS_MAPPINGS = {
  user: {
    active: 'Active' as const,
    inactive: 'Inactive' as const,
    suspended: 'Suspended' as const,
  },
  agent: {
    active: 'Published' as const,
    draft: AGENT_STATUS.DRAFT,
    review: 'Review' as const,
    published: 'Published' as const,
    archived: 'Archived' as const,
  },
  project: {
    active: PROJECT_STATUS.ACTIVE,
    draft: PROJECT_STATUS.DRAFT,
    archived: PROJECT_STATUS.ARCHIVED,
  },
  model: {
    active: RESOURCE_STATUS.ACTIVE,
    inactive: 'Inactive' as const,
    deprecated: RESOURCE_STATUS.DEPRECATED,
  },
  tool: {
    active: RESOURCE_STATUS.ACTIVE,
    inactive: 'Inactive' as const,
    deprecated: RESOURCE_STATUS.DEPRECATED,
  },
  knowledge: {
    synced: KNOWLEDGE_SYNC_STATUS.SYNCED,
    syncing: KNOWLEDGE_SYNC_STATUS.SYNCING,
    error: KNOWLEDGE_SYNC_STATUS.ERROR,
  },
  simulation: {
    queued: 'Running' as const,
    running: SIMULATION_STATUS.RUNNING,
    completed: 'Success' as const,
    failed: SIMULATION_STATUS.FAILED,
  },
} as const

// Generic creator info transformer
export const transformCreator = (apiEntity: BaseApiEntity): string => {
  return apiEntity.creator_name || apiEntity.created_by || 'Unknown'
}

// Generic timestamp transformer
export const transformTimestamps = (apiEntity: BaseApiEntity) => ({
  created: apiEntity.created_at,
  updated: apiEntity.updated_at,
})

// Generic status transformer
export const transformStatus = <T extends keyof typeof STATUS_MAPPINGS>(
  status: string | null | undefined,
  type: T
): string => {
  const mapping = STATUS_MAPPINGS[type]
  if (!status) {
    const firstValue = Object.values(mapping)[0]
    return String(firstValue ?? Object.values(mapping)[0] ?? '')
  }
  const statusKey = status.toLowerCase() as keyof (typeof STATUS_MAPPINGS)[T]
  const mappedValue = mapping[statusKey]
  return String(mappedValue ?? Object.values(mapping)[0] ?? '')
}

export const transformUserRole = (roles?: Array<{ name: string }>, ignorePlatformAdmin = true): string => {
  if (!roles || roles.length === 0) return 'Member'

  let displayRoles = roles
  if (ignorePlatformAdmin) {
    const nonPlatformAdminRoles = roles.filter(r => {
      const name = r.name.toLowerCase().replace(/[\s-]/g, '_')
      return name !== 'platform_admin' && name !== 'platformadmin'
    })
    if (nonPlatformAdminRoles.length > 0) {
      displayRoles = nonPlatformAdminRoles
    }
  }

  // If we didn't filter out platform admin, check for it
  if (!ignorePlatformAdmin || displayRoles === roles) {
    const hasPlatformAdmin = displayRoles.some((role) => {
      const name = role.name.toLowerCase().replace(/[\s-]/g, '_')
      return name === 'platform_admin' || name === 'platformadmin'
    })
    if (hasPlatformAdmin) {
      const platAdminRole = displayRoles.find((role) => {
        const name = role.name.toLowerCase().replace(/[\s-]/g, '_')
        return name === 'platform_admin' || name === 'platformadmin'
      })
      return platAdminRole ? platAdminRole.name : 'platform_admin'
    }
  }

  // Prioritize admin if present
  const hasAdmin = displayRoles.some((role) => {
    const name = role.name.toLowerCase().replace(/[\s-]/g, '_')
    return name === 'admin'
  })
  if (hasAdmin) {
    const adminRole = displayRoles.find((role) => {
      const name = role.name.toLowerCase().replace(/[\s-]/g, '_')
      return name === 'admin'
    })
    return adminRole ? adminRole.name : 'admin'
  }

  return displayRoles[0].name
}

// Transform project references
export const transformProjectReference = (
  projectId: string | null,
  projectMap: Map<string, { name: string }>
): string => {
  if (!projectId) return 'None'
  return projectMap.get(projectId)?.name || 'Unknown'
}

// Transform agent references
export const transformAgentReference = (
  agentId: string | null,
  agentMap: Map<string, { name: string }>
): string => {
  if (!agentId) return 'Unknown'
  return agentMap.get(agentId)?.name || 'Unknown'
}

// Generic entity transformer factory
export function createEntityTransformer<
  TApi extends BaseApiEntity,
  TUI extends BaseUIEntity,
>(options: {
  statusType?: keyof typeof STATUS_MAPPINGS
  additionalFields?: (apiEntity: TApi, context?: unknown) => Partial<TUI>
  contextTransformers?: {
    projects?: Map<string, { name: string }>
    agents?: Map<string, { name: string }>
    users?: Map<string, { name: string;[key: string]: unknown }>
  }
}) {
  return (apiEntity: TApi, context?: unknown): TUI => {
    const baseTransform: TUI = {
      id: apiEntity.id,
      created: apiEntity.created_at,
      updated: apiEntity.updated_at,
      creator: transformCreator(apiEntity),
      // Carry the per-user favorite flag through so list-row stars render correctly.
      is_fav: (apiEntity as TApi & { is_fav?: boolean }).is_fav ?? false,
    } as TUI

    // Add status if applicable
    if (options.statusType && 'status' in apiEntity) {
      const apiEntityWithStatus = apiEntity as TApi & { status?: string | null }
        ; (baseTransform as TUI & { status?: unknown }).status = transformStatus(
          apiEntityWithStatus.status,
          options.statusType
        )
    }

    // Add additional fields
    if (options.additionalFields) {
      Object.assign(baseTransform, options.additionalFields(apiEntity, context))
    }

    return baseTransform
  }
}

// Specific entity transformers using the factory
export const createProjectTransformer = createEntityTransformer<
  ProjectWithBase,
  BaseUIEntity & { name: string; description: string; members: number }
>({
  statusType: 'project',
  additionalFields: (apiProject: ProjectWithBase) => ({
    name: apiProject.name,
    description: apiProject.description || '',
    members: 0, // Will be set by caller
  }),
})

export const createAgentTransformer = createEntityTransformer<
  AgentWithBase & { project_name?: string | null },
  BaseUIEntity & {
    name: string
    project: string
    project_id: string
    version: number
    framework: string
    registry_status?: string
    description: string
    created_by?: string
    icon_file_id?: string | null
  }
>({
  statusType: 'agent',
  additionalFields: (apiAgent: AgentWithBase & { project_name?: string | null }, context?: unknown) => {
    const projectMap = context as Map<string, { name: string }> | undefined
    return {
      name: apiAgent.name,
      description: apiAgent.description || '',
      project:
        apiAgent.project_name ||
        transformProjectReference(apiAgent.project_id, projectMap || new Map()),
      project_id: apiAgent.project_id,
      version: apiAgent.version,
      framework: apiAgent.framework || 'custom',
      registry_status: (apiAgent as AgentWithBase & { registry_status?: string }).registry_status || 'unregistered',
      created_by: apiAgent.created_by,
      icon_file_id: (apiAgent as AgentWithBase).icon_file_id ?? null,
    }
  },
})

export const createModelTransformer = createEntityTransformer<
  ModelWithBase,
  BaseUIEntity & {
    name: string
    provider: string
    modelId: string
    region: string
    createdBy?: string
    isDefault?: boolean
    modelType?: string
  }
>({
  statusType: 'model',
  additionalFields: (apiModel: ModelWithBase) => ({
    name: apiModel.display_name,
    provider: apiModel.provider,
    modelId: apiModel.model_id,
    region: 'Global', // Display label - API doesn't provide region information
    createdBy: apiModel.created_by, // Add for RBAC checks
    isDefault: apiModel.is_default, // Whether this is the default model
    modelType: apiModel.model_type || 'llm', // 'llm' or 'embedding'
  }),
})

export const createToolTransformer = createEntityTransformer<
  ToolWithBase,
  BaseUIEntity & { name: string; type: string; endpoint: string; auth: string; created_by?: string }
>({
  additionalFields: (apiTool: ToolWithBase) => {
    const typeMap: Record<string, 'OpenAPI' | 'MCP' | 'Function' | 'HTTP API' | 'Workflow'> = {
      openapi: 'OpenAPI',
      mcp: 'MCP',
      function: 'Function',
      http: 'HTTP API',
      workflow: 'Workflow',
    }

    const authType = apiTool.auth_config?.type || 'None'
    const authDisplay = typeof authType === 'string' ? authType : 'None'

    return {
      name: apiTool.display_name,
      type: typeMap[apiTool.type?.toLowerCase() || 'openapi'] || 'OpenAPI',
      endpoint: apiTool.endpoint,
      auth: authDisplay,
      created_by: apiTool.created_by, // Add for RBAC checks
    }
  },
})

export const createKnowledgeTransformer = createEntityTransformer<
  RegisteredTableWithBase,
  BaseUIEntity & { name: string; type: string; provider: string; documents: number }
>({
  statusType: 'knowledge',
  additionalFields: (apiConnector: RegisteredTableWithBase) => {
    const typeMap: Record<string, 'Vector' | 'SQL' | 'Graph' | 'Document'> = {
      vector_db: 'Vector',
      database: 'SQL',
    }

    // RegisteredTable doesn't have connection_uri or type directly
    // It references a connector via connector_connection_id
    // For now, default to Vector type and Unknown provider
    // Provider info would need to come from the connector connection if needed
    const provider = 'Unknown'

    return {
      name: apiConnector.display_name,
      type: typeMap['vector_db'] || 'Vector',
      provider,
      documents: apiConnector.row_count ?? 0, // Use row_count from API if available, otherwise 0
    }
  },
})

export const createPromptTransformer = createEntityTransformer<
  PromptWithBase,
  BaseUIEntity & { name: string; content: string; category: string }
>({
  additionalFields: (apiPrompt: PromptWithBase) => ({
    name: apiPrompt.name,
    description: apiPrompt.description || undefined,
    content: apiPrompt.content || '', // Content may be optional in list view
    category: apiPrompt.category || 'Uncategorized',
    // Usage field removed - API doesn't provide usage statistics
  }),
})

// User transformers (special case - different base structure)
export const createPendingUserTransformer = (apiUser: User) => ({
  id: apiUser.id,
  name: apiUser.display_name,
  email: apiUser.email,
  external_id: apiUser.external_id || null,
  requested: formatRelativeTime(apiUser.created_at),
  requestedRole: transformUserRole(apiUser.roles),
  status: 'Pending' as const,
  created_by_name: apiUser.created_by_name || null,
})

export const createRejectedUserTransformer = (apiUser: User) => ({
  id: apiUser.id,
  name: apiUser.display_name,
  email: apiUser.email,
  requested: formatRelativeTime(apiUser.created_at),
  requestedRole: transformUserRole(apiUser.roles),
  rejected: formatRelativeTime(apiUser.updated_at),
  status: 'Rejected' as const, // Display status for rejected users (maps to USER_STATUS.REJECTED)
  created_by_name: apiUser.created_by_name || null,
})

export const createRegisteredUserTransformer = (apiUser: User) => ({
  id: apiUser.id,
  name: apiUser.display_name,
  email: apiUser.email,
  external_id: apiUser.external_id || null,
  role: transformUserRole(apiUser.roles),
  roleIds: apiUser.roles?.map((r) => r.id) || [],
  status: transformStatus(apiUser.status?.toLowerCase() || 'active', 'user'),
  lastActive: apiUser.last_login_at
    ? formatRelativeTime(apiUser.last_login_at)
    : 'Never',
  joined: formatJoinedTime(apiUser.created_at),
  created_by_name: apiUser.created_by_name || null,
  created: apiUser.created_at,
  updated: apiUser.updated_at,
})

export const createRoleTransformer = (
  apiRole: Role,
  userCount: number,
  tenantName?: string | null
) => {
  // Convert permissions object to array format for display
  let permissionsArray: PermissionDisplay[] = []
  if (apiRole.permissions && typeof apiRole.permissions === 'object') {
    permissionsArray = Object.entries(apiRole.permissions).map(([key, value]) => ({
      feature: key,
      actions: (typeof value === 'object' && value !== null
        ? value
        : { view: false, create: false, edit: false, delete: false }) as Record<string, boolean>,
    }))
  }

  return {
    id: apiRole.id,
    name: apiRole.name,
    members: userCount,
    description: apiRole.description || '',
    permissions: permissionsArray,
    updated: apiRole.updated_at,
    created: apiRole.created_at,
    creator: apiRole.created_by || 'Unknown',
    tenantId: apiRole.tenant_id || null,
    tenantName: tenantName || (apiRole.tenant_id ? 'Unknown Tenant' : 'Global'),
  }
}

export const createEnvironmentTransformer = (apiEnv: Environment) => {
  const overrides = []
  if (apiEnv.tool_overrides)
    overrides.push(`Tools: ${Object.keys(apiEnv.tool_overrides).join(', ')}`)
  if (apiEnv.knowledge_connector_overrides)
    overrides.push(`Knowledge: ${Object.keys(apiEnv.knowledge_connector_overrides).join(', ')}`)
  if (apiEnv.variables) overrides.push(`Variables: ${JSON.stringify(apiEnv.variables)}`)

  return {
    id: apiEnv.id,
    name: apiEnv.name,
    description: apiEnv.description || '',
    overrides: overrides.join(' · ') || 'None',
    createdBy: apiEnv.created_by || 'Unknown',
    updated: apiEnv.updated_at,
    created: apiEnv.created_at,
    status: 'Active' as const, // UI-only field: API doesn't provide status, defaulting to ACTIVE
  }
}

export const createSimulationTransformer = (
  apiSim: Simulation,
  agentMap: Map<string, Agent>,
  envMap: Map<string, Environment>
) => {
  const agent = agentMap.get(apiSim.agent_id)
  const env = apiSim.environment_id ? envMap.get(apiSim.environment_id) : null

  const startedDate = apiSim.started_at
    ? new Date(apiSim.started_at / 1000)
    : new Date(apiSim.created_at * 1000)
  const now = Date.now()
  const diffMs = now - startedDate.getTime()

  // Under a minute keeps the 'Just now' default
  let startedStr = 'Just now'
  if (diffMs >= 60000 && diffMs < 3600000) startedStr = `${Math.floor(diffMs / 60000)} minutes ago`
  else if (diffMs >= 3600000 && diffMs < 86400000) startedStr = `${Math.floor(diffMs / 86400000)} days ago`

  const duration =
    apiSim.ended_at && apiSim.started_at
      ? `${Math.floor((apiSim.ended_at - apiSim.started_at) / 1000000)}s`
      : '...'

  const statusMap: Record<string, 'Running' | 'Success' | 'Failed'> = {
    queued: 'Running',
    running: 'Running',
    completed: 'Success',
    failed: 'Failed',
  }

  return {
    id: apiSim.id,
    agent: agent?.name || 'Unknown',
    environment: env?.name || 'None',
    status: statusMap[apiSim.status?.toLowerCase()] || 'Running',
    duration,
    tokens: '0', // UI-only field: API doesn't provide token count, defaulting to '0'
    toolsUsed: 0, // UI-only field: API doesn't provide tools used count, defaulting to 0
    started: startedStr,
    creator: apiSim.created_by || 'Unknown',
    created: apiSim.created_at,
    updated: apiSim.updated_at,
  }
}
